import React, { useEffect, useState } from 'react'
import { Elements } from "@stripe/react-stripe-js";
import {
  Box, Chip, Container, Typography, Divider, Link, Paper,
  Table, TableBody, TableCell, TableContainer, TableRow,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import InsertChartRoundedIcon from '@mui/icons-material/InsertChartRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SsidChartRoundedIcon from '@mui/icons-material/SsidChartRounded';
import { dataConstants } from '../../constants';
import SearchUnified from '../_common/SearchUnified';
import FAQs from './FAQs';
import useAuth from '../../hook/auth';
import { loadStripe } from "@stripe/stripe-js";
import LandingCheckout from "./LandingCheckout";
import GrufityModel from './GrufityModel';
import SectionHeading from '../_common/SectionHeading';
import FeatureCard from '../_common/FeatureCard';
import applestock from '../../static/images/page/home/applestock.png'
import houingmarket from '../../static/images/page/home/houingmarket.png'
import berkshirefund from '../../static/images/page/home/berkshirefund.png'
import * as send from '../../../lib/analytics/send';
import { useRouter } from 'next/navigation';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY);



const { PLAN_COMPARISON } = dataConstants || {};

const popularTickers = ['AAPL', 'TSLA', 'AMZN', 'GOOG', 'MSFT', 'NVDA'];
const popularFunds = [
  { fcik: '1067983', fundName: 'Warren Buffett' },
  { fcik: '1697748', fundName: 'Cathie Wood' },
  { fcik: '1350694', fundName: 'Ray Dalio' },
  { fcik: '1029160', fundName: 'George Soros' },
];

const Landing = (props) => {
  const { pricePlans, pageLoading, modelData } = props || {}
  const { user } = useAuth() || {}
  const router = useRouter()
  const { reloadUserInfo } = user || {}
  const isPremium = reloadUserInfo?.customAttributes && (JSON.parse(reloadUserInfo?.customAttributes)?.userType === "PREMIUM" || JSON.parse(reloadUserInfo?.customAttributes)?.userType === "PROFESSIONAL") ? true : false;

  const pricing_comparison_rows = PLAN_COMPARISON;
  const [showSearch, setShowSearch] = useState(false);
  const [currency_type, set_currency_type] = useState('usd');

  useEffect(() => {
    const currentDate = new Date();
    if (`${currentDate}`.includes('India Standard Time')) {
      set_currency_type('inr')
    } else {
      set_currency_type('usd')
    }
  }, [new Date()]);

  return (
    <Box component="div">
      {modelData && modelData.length > 0 && (
        <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: '#ffffff' }}>
          <Container maxWidth="lg">
            <GrufityModel modelData={modelData} />
          </Container>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: { xs: 8, md: 12 },
          bgcolor: '#ffffff',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '120%',
            height: '100%',
            background: 'radial-gradient(ellipse at 50% 0%, rgba(37, 99, 235, 0.04) 0%, transparent 60%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="sm" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{ mb: 2 }}
          >
            Discover The Joy of{' '}
            <Typography
              component="span"
              sx={{
                fontWeight: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                letterSpacing: 'inherit',
                color: 'secondary.main',
              }}
            >
              Investing
            </Typography>
          </Typography>

          <Typography
            variant="subtitle1"
            component="p"
            sx={{ mb: 5, maxWidth: 480, mx: 'auto' }}
          >
            Explore, screen, and track your portfolio with institutional-grade data. Invest smarter, not harder.
          </Typography>

          {/* Search Bar */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 3,
              px: { xs: 0.5, md: 1 },
              py: 0.5,
              maxWidth: 560,
              mx: 'auto',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
              transition: 'box-shadow 200ms ease, border-color 200ms ease',
              '&:focus-within': {
                borderColor: 'secondary.main',
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
              },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SearchUnified
                setShowSearch={setShowSearch}
                removebackdrop={true}
                placeholder="Search stocks or funds..."
                dataEventFrom={"homepage"}
                variant="light"
              />
            </Box>
          </Paper>

          {/* Popular Links */}
          <Box sx={{ mt: 4.5 }}>
            <Typography variant="caption" component="div" sx={{ mb: 1.5 }}>
              Popular Tickers:{' '}
              {popularTickers.map((d) => (
                <Chip
                  key={d}
                  label={d}
                  component="a"
                  href={`/stock/${d}`}
                  clickable
                  size="small"
                  variant="outlined"
                  onClick={(e) => send.uevent(e)}
                  data-event-name="href"
                  data-event-category="homepage"
                  data-event-label={`/stock/${d}`}
                  sx={{
                    mx: 0.4,
                    my: 0.4,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    borderColor: 'grey.200',
                    color: 'text.link',
                    transition: 'all 150ms ease',
                    '&:hover': { borderColor: 'secondary.main', bgcolor: '#eff6ff' },
                  }}
                />
              ))}
            </Typography>
            <Typography variant="caption" component="div" sx={{ mt: 2 }}>
              Popular Funds:{' '}
              {popularFunds.map((d) => (
                <Chip
                  key={d.fcik}
                  label={d.fundName}
                  component="a"
                  href={`/funds/${d.fcik}`}
                  clickable
                  size="small"
                  variant="outlined"
                  onClick={(e) => send.uevent(e)}
                  data-event-name="href"
                  data-event-category="homepage"
                  data-event-label={`/funds/${d.fcik}`}
                  sx={{
                    mx: 0.4,
                    my: 0.4,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    borderColor: 'grey.200',
                    color: 'text.link',
                    transition: 'all 150ms ease',
                    '&:hover': { borderColor: 'secondary.main', bgcolor: '#eff6ff' },
                  }}
                />
              ))}
            </Typography>
          </Box>
        </Container>
      </Box>
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionHeading
                overline="Why Grufity"
                title="Experience Powerful Data"
                subtitle="Grufity provides the historical trend of financial metrics — not just point-in-time values. Knowing the history of a stock's price-to-sales ratio puts today's value in the right context."
                sx={{ mb: 0 }}
              />
              <Box
                component="blockquote"
                sx={{
                  mt: 3,
                  pl: 3,
                  borderLeft: '3px solid',
                  borderColor: 'secondary.main',
                  fontStyle: 'italic',
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.description' }}>
                  {`"...eliminates the gap between hedge funds and retail investors."`}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: 'grey.100',
                  borderRadius: 3,
                  overflow: 'hidden',
                  height: { xs: 300, md: 450 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '80%',
                    height: '80%',
                    '& img': {
                      position: 'absolute',
                      borderRadius: 2,
                      width: '65%',
                      height: 'auto',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                      filter: 'none !important',
                      WebkitFilter: 'none !important',
                    },
                    '&:hover img:nth-of-type(1)': { transform: 'translate(-10%, -5%) rotate(-3deg)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)' },
                    '&:hover img:nth-of-type(2)': { transform: 'translate(5%, 0%) rotate(1deg)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)' },
                    '&:hover img:nth-of-type(3)': { transform: 'translate(15%, 5%) rotate(3deg)', boxShadow: '0 12px 40px rgba(0,0,0,0.15)' },
                  }}
                >
                  <img
                    src={typeof houingmarket === 'object' ? houingmarket.src : houingmarket}
                    alt="Housing market data"
                    style={{ top: '15%', left: '0%', zIndex: 1 }}
                  />
                  <img
                    src={typeof berkshirefund === 'object' ? berkshirefund.src : berkshirefund}
                    alt="Berkshire fund analysis"
                    style={{ top: '5%', left: '20%', zIndex: 2 }}
                  />
                  <img
                    src={typeof applestock === 'object' ? applestock.src : applestock}
                    alt="Apple stock metrics"
                    style={{ top: '-5%', left: '35%', zIndex: 3 }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            overline="Easy Access"
            title="All the financial data and tools you need, in one place"
            accentText="financial data and tools"
            align="center"
            maxWidth={600}
          />
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FeatureCard icon={<SsidChartRoundedIcon />} title="Stocks">
                The best place to do your{' '}
                <Link href="/stock/AAPL" underline="hover" sx={{ color: 'text.link', fontWeight: 500 }}>due diligence</Link>
                {' '}on any stock. Detailed historical trends on financial ratios — PE, PS, ROE, ROA, Debt/Equity, and more.
                <Typography variant="body2" component="div" sx={{ mt: 1.5, color: 'text.description' }}>
                  Insider trading trends and alerts. Are funds buying or selling? Latest financial reports and news.
                </Typography>
              </FeatureCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FeatureCard icon={<PaidRoundedIcon />} title="Funds">
                A powerful tool for shadow investing. Coverage of all major hedge funds.
                <Typography variant="body2" component="div" sx={{ mt: 1.5, color: 'text.description' }}>
                  Current holdings, recent buys and sells, position size changes, and historical trends of how funds change their positions over time.
                </Typography>
              </FeatureCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FeatureCard icon={<InsertChartRoundedIcon />} title="Economy">
                <Box component="span" sx={{ color: 'text.link', fontWeight: 500 }}>Yield Curve:</Box> Daily updated analysis and recession prediction.
                <br /><Box component="span" sx={{ color: 'text.link', fontWeight: 500 }}>Inflation:</Box> Quick view of CPI, PCE, and trends.
                <br /><Box component="span" sx={{ color: 'text.link', fontWeight: 500 }}>GDP:</Box> Breakdown and past trends.
                <br /><Box component="span" sx={{ color: 'text.link', fontWeight: 500 }}>Housing:</Box> Detailed charts on the sector that impacts us all.
                <br /><Box component="span" sx={{ color: 'text.link', fontWeight: 500 }}>Consumer:</Box> Two-thirds of the economy is consumption.
              </FeatureCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <SectionHeading
            align="center"
            overline="Pricing"
            title={`Institutional-grade data — for the price of a coffee`}
            accentText="price of a coffee"
            maxWidth={650}
          />
          <Typography variant="body2" sx={{ mb: 5, color: 'text.description', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
            Get Full Access to Grufity
          </Typography>

          <Elements stripe={stripePromise}>
            <LandingCheckout pricePlans={pricePlans} />
          </Elements>

          <Box
            sx={{
              mt: 8,
              p: { xs: 4, md: 5 },
              bgcolor: 'grey.50',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="h3" component="div" sx={{ mb: 2 }}>
              {`"Rule `}
              <Typography component="span" sx={{ fontWeight: 'inherit', fontSize: 'inherit', color: 'secondary.main' }}>
                No. 1
              </Typography>
              {` is never lose money."`}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.description' }}>
              — Warren Buffett
            </Typography>
            <Typography variant="h4" component="div" sx={{ mt: 4 }}>
              Never buy a stock without a complete look at{' '}
              <Typography component="span" sx={{ fontWeight: 'inherit', fontSize: 'inherit', color: 'secondary.main' }}>
                Grufity
              </Typography>.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            overline="Features"
            title="A 360° view on Stocks and Economy"
            accentText="Stocks and Economy"
          />
          <TableContainer
            sx={{
              mt: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.200',
              boxShadow: 'none',
            }}
          >
            <Table aria-label="features table" size="small" sx={{ bgcolor: '#fff' }}>
              <TableBody>
                {pricing_comparison_rows && pricing_comparison_rows.map((row, idx) => (
                  <TableRow
                    key={row.feature_name || idx}
                    sx={{
                      '&:nth-of-type(even)': { bgcolor: 'grey.50' },
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        py: 2,
                        '& a': { fontSize: '0.875rem', color: 'text.link', fontWeight: 600, textDecoration: 'none' },
                        '& a:hover': { textDecoration: 'underline' },
                      }}
                    >
                      <Typography variant="body2" component="div" sx={{ fontWeight: 500, color: 'text.primary' }} dangerouslySetInnerHTML={{ __html: row.feature_name }} />
                      {row.feature_desc && (
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ mt: 0.5, color: 'text.description', '& a': { fontSize: '0.75rem', color: 'text.link', fontWeight: 500, textDecoration: 'none' } }}
                          dangerouslySetInnerHTML={{ __html: row.feature_desc }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 80 }}>
                      <CheckCircleRoundedIcon sx={{ fontSize: '1.4rem', color: 'success.main' }} />
                      {row.premium_desc && (
                        <Typography variant="caption" component="div" sx={{ color: 'text.description', mt: 0.5 }}>
                          {row.premium_desc}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
            {`Don't just follow experts`}
          </Typography>
          <Typography
            variant="h2"
            component="p"
            sx={{ color: 'secondary.main', mb: 4 }}
          >
            Aim to be one
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 640,
              mx: 'auto',
              color: 'text.description',
              lineHeight: 1.8,
            }}
          >
            If you have been mostly looking at the price chart and hoping to make a quick buck on a stock,
            then chances are that you haven't yet discovered the true joy of investing.
            Price charts are important but they are just one clue out of many. The real joy of investing is
            in exploring many stocks and finding a few gems that will multiply your investments over the years.
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <FAQs
            pricePlans={pricePlans}
            header={
              <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography variant="overline" component="div" sx={{ mb: 1 }}>
                  Support
                </Typography>
                <Typography variant="h3" component="h2" sx={{ mb: 2 }}>
                  Frequently Asked Questions
                </Typography>
                <Divider sx={{ width: 60, mx: 'auto', borderColor: 'secondary.main', borderWidth: 1.5 }} />
              </Box>
            }
          />
        </Container>
      </Box>
    </Box>
  )
}

export default Landing
